import { useState, useEffect, useCallback } from 'react';
import { Download, Loader2, CheckCircle, AlertCircle, X } from 'lucide-react';
import { exportComplaints } from '../../api';
import type { ComplaintStatus } from '../../api';

interface ExportButtonProps {
  startDate?: number;
  endDate?: number;
  status?: ComplaintStatus | 'all';
  storeNumbers?: string[] | null;
  search?: string;
}

interface ExportFeedback {
  type: 'success' | 'error';
  message: string;
}

function formatDateLabel(timestamp?: number): string {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const MAX_EXPORT_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * Mirrors the backend clamping logic so the feedback message
 * accurately reflects the date window the server actually used.
 */
function getEffectiveDateRange(startDate?: number, endDate?: number): { start: number; end: number } {
  const now = Date.now();
  const end = endDate ?? now;
  let start = startDate ?? end - MAX_EXPORT_WINDOW_MS;

  if (end - start > MAX_EXPORT_WINDOW_MS) {
    start = end - MAX_EXPORT_WINDOW_MS;
  }
  return { start, end };
}

function buildDateRangeLabel(startDate?: number, endDate?: number): string {
  const { start, end } = getEffectiveDateRange(startDate, endDate);
  return `${formatDateLabel(start)} – ${formatDateLabel(end)}`;
}

const AUTO_DISMISS_MS = 5000;
const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export function ExportButton({ startDate, endDate, status, storeNumbers, search }: ExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [feedback, setFeedback] = useState<ExportFeedback | null>(null);

  useEffect(() => {
    if (!feedback) return;
    const timer = setTimeout(() => setFeedback(null), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [feedback]);

  const dismiss = useCallback(() => setFeedback(null), []);

  const handleExport = async () => {
    if (isExporting) return;

    setIsExporting(true);
    setFeedback(null);
    try {
      const blob = await exportComplaints({
        startDate,
        endDate,
        status: status && status !== 'all' ? status : undefined,
        storeNumbers: storeNumbers ?? undefined,
        search: search || undefined,
      });

      if (blob.size === 0) {
        setFeedback({
          type: 'success',
          message: `No complaints found for ${buildDateRangeLabel(startDate, endDate)}`,
        });
        return;
      }

      const downloadBlob = new Blob([blob], { type: XLSX_CONTENT_TYPE });
      const url = window.URL.createObjectURL(downloadBlob);
      const link = document.createElement('a');
      link.href = url;

      const date = new Date().toISOString().split('T')[0];
      link.download = `complaints-${date}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setFeedback({
        type: 'success',
        message: `Exported complaints (${buildDateRangeLabel(startDate, endDate)})`,
      });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to export complaints',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={handleExport}
        disabled={isExporting}
        title="Export Excel"
        aria-label="Export complaints as Excel"
        data-testid="complaints-btn-export"
        className="inline-flex items-center justify-center gap-2 p-2.5 min-h-[44px] min-w-[44px] sm:px-3 sm:py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isExporting ? (
          <>
            <Loader2 className="w-4 h-4 text-gray-500 animate-spin" />
            <span className="hidden sm:inline">Exporting...</span>
          </>
        ) : (
          <>
            <Download className="w-4 h-4 text-gray-500" />
            <span className="hidden sm:inline">Export</span>
          </>
        )}
      </button>

      {feedback && (
        <div
          role="status"
          aria-live="polite"
          className={`absolute right-0 top-full mt-2 z-50 flex items-start gap-2 min-w-[280px] max-w-[360px] p-3 rounded-lg shadow-lg border text-sm animate-fade-in ${
            feedback.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {feedback.type === 'success' ? (
            <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          )}
          <span className="flex-1">{feedback.message}</span>
          <button
            onClick={dismiss}
            className="shrink-0 p-0.5 rounded hover:bg-black/5"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
